/**
 * Planning Pool Query Hooks (Epic D-1 — Fundament, Story D-1.4)
 *
 * Zelfde query-keys-factory + invalidatie-patroon als
 * `usePlanningCapacity.ts` (AD-9/AD-18): elke mutatie invalideert de
 * statusquery, geen inline `apiRequest` in componenten.
 */

import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { planningPoolApi } from '../../../lib/planningPoolApi';
import { planningBoardKeys } from './usePlanningBoard';
import type { SetPlanningPoolOverrideInput } from '@functieloonhuis/shared';

export const planningPoolKeys = {
  all: ['planningPool'] as const,
  status: (date: string) => [...planningPoolKeys.all, 'status', date] as const,
};

/**
 * Review-bevinding D-1.4: het planbord hangt sinds deze story óók van de
 * handmatige stand af (`getBoard` past de poort toe op de lege cellen) — een
 * gecachet bord zou anders tot zijn eigen refetch de oude planbaarheid tonen.
 */
function invalidatePoolReads(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: planningPoolKeys.all });
  queryClient.invalidateQueries({ queryKey: planningBoardKeys.all });
}

/**
 * FR-9/FR-10 — de planbaarheid van élke medewerker op `date`.
 *
 * `enabled` bestaat zodat een component dat de hook onvoorwaardelijk moet
 * aanroepen (rules of hooks) de query kan uitzetten voor wie het veld nooit
 * te zien krijgt — anders vuurt elke medewerker die een medewerkerdetail
 * opent een aanroep af die op een 403 stukloopt, plus retries.
 */
export function usePoolStatus(date: string, enabled: boolean = true) {
  return useQuery({
    queryKey: planningPoolKeys.status(date),
    queryFn: () => planningPoolApi.listStatus(date),
    enabled,
  });
}

export function useSetPoolOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      employeeId,
      input,
    }: {
      employeeId: number;
      input: SetPlanningPoolOverrideInput;
    }) => planningPoolApi.setOverride(employeeId, input),
    onSuccess: () => {
      invalidatePoolReads(queryClient);
    },
  });
}

export function useClearPoolOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (employeeId: number) => planningPoolApi.clearOverride(employeeId),
    onSuccess: () => {
      invalidatePoolReads(queryClient);
    },
  });
}
